import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Divider, Input, Upload } from 'antd';
import { Button, Modal } from '@insight-health-ai/roseate/components';
import { FileAttachment02, Trash01 } from '@insight-health-ai/roseate/icons';
import { LoadingOutlined, InboxOutlined } from '@ant-design/icons';
import {
  useGetEncounterDocumentList,
  useLinkEncounterDocuments,
  useUnlinkEncounterDocuments,
} from 'src/hooks/encounter';
import { mergePatientTextDocuments } from 'src/utils/encounter';
import { PatientDocument } from 'src/models';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';
import { NonOptional } from '@insight-health-ai/roseate/utils';
import {
  useCreatePatientDocument,
  useUpdatePatientDocument,
} from 'src/hooks/patient';
import { PatientFileDocument, PatientFileDocumentProps } from 'src/components';
import {
  useLinkAppointmentDocuments,
  useUnlinkAppointmentDocuments,
} from 'src/hooks/appointment';
import styled from '@emotion/styled';

type Attachment = {
  isError?: boolean;
  isLoading?: boolean;
  document: NonOptional<
    Partial<PatientDocument>,
    'patient_document_id' | 'name' | 'type' | 'mime_type' | 'size'
  >;
};

const minViewportVisibility = 64;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    height: calc(100vh - ${minViewportVisibility}px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    height: 100%;
  }
`;

export type PreChartDocumentsProps = {
  source: PatientFileDocumentProps['source'];
  encounterId: string;
  getTrigger?: (currentState: {
    attachments: Attachment[];
    notesDocument: {
      id: string | null;
      content: string;
    };
    isLoading: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
  }) => React.ReactNode;
  onDocumentsChange?: () => void;
  onChange?: (documents: {
    documents: PatientDocument[];
    notes: {
      id: string | null;
      content: string;
    };
  }) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const tempDocumentIdPrefix = '__temp__';
const maxAllowedUploadSize = 400000;
const bytesPerCharacter = 4;

export const PreChartDocuments = ({
  encounterId,
  source,
  getTrigger,
  onChange,
  onDocumentsChange,
  onOpenChange,
}: PreChartDocumentsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notesDocument, setNotesDocument] = useState<{
    id: string | null;
    content: string;
  }>({ id: null, content: '' });
  const [attachments, setAttachments] = useState<
    {
      isError?: boolean;
      isLoading?: boolean;
      document: NonOptional<
        Partial<PatientDocument>,
        'patient_document_id' | 'name' | 'type' | 'mime_type' | 'size'
      >;
    }[]
  >([]);

  const { data: documentsData, isLoading: isLoadingDocuments } =
    useGetEncounterDocumentList(
      { encounterId },
      { refetchOnWindowFocus: false }
    );
  const { mutateAsync: createPatientDocument } = useCreatePatientDocument();

  const { mutateAsync: unlinkEncounterDocuments } =
    useUnlinkEncounterDocuments();
  const { mutateAsync: unlinkAppointmentDocuments } =
    useUnlinkAppointmentDocuments();
  const { mutateAsync: linkDocumentToEncounter } = useLinkEncounterDocuments();
  const { mutateAsync: linkDocumentToAppointment } =
    useLinkAppointmentDocuments();
  const { mutateAsync: updatePatientDocument } = useUpdatePatientDocument();

  const { user } = useAuth();

  const currentTotalUploadedSize = useMemo(() => {
    return attachments.reduce((acc, { document }) => {
      return acc + (document.size || 0);
    }, 0);
  }, [attachments]);

  const maxAllowedNotesLength = Math.floor(
    (maxAllowedUploadSize - currentTotalUploadedSize) / bytesPerCharacter
  );

  const validateFile = async (file: File) => {
    const fileObj = file;

    const newTotalHistorySize =
      currentTotalUploadedSize +
      (notesDocument.content?.length || 0) * bytesPerCharacter +
      (fileObj.size! || 0);
    if (newTotalHistorySize > maxAllowedUploadSize) {
      analytics.track(
        `Upload limit exceeded In ${
          source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
        } History Modal`,
        {
          totalUploadedFileSize: newTotalHistorySize,
          email: user?.email,
        }
      );
      Modal.warning({
        title: 'Upload limit exceeded',
        content: `The total size of all files uploaded should not exceed ${
          maxAllowedUploadSize / 1000
        }KB.`,
      });
      return false;
    }

    // check if file is already in attachments
    const similarFileExists = attachments.some(
      (attachment) =>
        attachment.document.name === fileObj.name &&
        attachment.document.mime_type === fileObj.type &&
        attachment.document.size === fileObj.size
    );
    if (similarFileExists) {
      return await Modal.confirm({
        centered: true,
        title: 'Similar file already attached',
        content: 'Do you still want to upload?',
        primaryAction: {
          label: 'Proceed',
          onClick: () => {
            analytics.track(
              `Clicked Proceed On Duplicate File Alert In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                email: user?.email,
              }
            );
          },
        },
        onClose: () => {
          analytics.track(
            `Clicked Cancel On Duplicate File Alert In ${
              source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
            } History Modal`,
            {
              email: user?.email,
            }
          );
        },
      });
    }

    return true;
  };

  const createFileDocument = async (file: File) => {
    const fileObj = file;
    const tempDocumentId = `${tempDocumentIdPrefix}${Date.now()}`;
    setAttachments([
      ...attachments,
      {
        isLoading: true,
        document: {
          patient_document_id: tempDocumentId,
          name: fileObj.name,
          type: 'file',
          mime_type: fileObj.type!,
          size: fileObj.size!,
        },
      },
    ]);

    try {
      const newDoc = await createPatientDocument({
        type: 'file',
        file: fileObj,
        size: fileObj.size,
        source,
      });
      if (source === 'pre_chart_history') {
        await linkDocumentToEncounter({
          encounterId,
          documentIds: [newDoc.patient_document_id],
        });
      } else if (source === 'vca_patient_history') {
        await linkDocumentToAppointment({
          appointmentId: encounterId,
          documentIds: [newDoc.patient_document_id],
        });
      }
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.document.patient_document_id === tempDocumentId
            ? { document: newDoc, isLoading: false }
            : attachment
        )
      );
      onDocumentsChange?.();
    } catch (error) {
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.document.patient_document_id === tempDocumentId
            ? { ...attachment, isLoading: false, hasError: true }
            : attachment
        )
      );
    }
  };

  const updateNotesDocument = async (content: string) => {
    if (!notesDocument.id) {
      if (!content) {
        return;
      }
      const document = await createPatientDocument({
        content,
        type: 'text',
        source,
      });
      if (source === 'pre_chart_history') {
        await linkDocumentToEncounter({
          encounterId,
          documentIds: [document.patient_document_id],
        });
      } else if (source === 'vca_patient_history') {
        await linkDocumentToAppointment({
          appointmentId: encounterId,
          documentIds: [document.patient_document_id],
        });
      }
      setNotesDocument((prev) => ({
        ...prev,
        id: document.patient_document_id,
      }));
    } else {
      await updatePatientDocument({
        patientDocumentId: notesDocument.id,
        content,
      });
    }
    onDocumentsChange?.();
  };

  const deleteDocument = async (attachment: (typeof attachments)[number]) => {
    const documentId = attachment.document.patient_document_id;
    if (!documentId?.includes(tempDocumentIdPrefix)) {
      const confirmed = await Modal.confirm({
        appearance: 'error',
        title: 'Delete patient document?',
        content: 'Are you sure you want to delete this document?',
        primaryAction: {
          label: 'Delete',
          onClick: async () => {
            if (source === 'pre_chart_history') {
              await unlinkEncounterDocuments({
                encounterId,
                documentIds: [documentId],
              });
            } else if (source === 'vca_patient_history') {
              await unlinkAppointmentDocuments({
                appointmentId: encounterId,
                documentIds: [documentId],
              });
            }
          },
        },
      });
      if (!confirmed) {
        return;
      }
    }
    setAttachments(
      attachments.filter(
        ({ document }) => document.patient_document_id !== documentId
      )
    );
    onDocumentsChange?.();
  };

  const deleteAllDocuments = async () => {
    const confirmed = await Modal.confirm({
      appearance: 'error',
      title: 'Delete all documents?',
      content: 'This will remove all documents from the encounter.',
      primaryAction: {
        label: 'Delete',
        onClick: async () => {
          if (source === 'pre_chart_history') {
            await unlinkEncounterDocuments({
              encounterId,
              documentIds: attachments.map(
                ({ document }) => document.patient_document_id
              ),
            });
          } else if (source === 'vca_patient_history') {
            await unlinkAppointmentDocuments({
              appointmentId: encounterId,
              documentIds: attachments.map(
                ({ document }) => document.patient_document_id
              ),
            });
          }
        },
      },
    });
    if (!confirmed) {
      return;
    }
    setAttachments([]);
    onDocumentsChange?.();
  };

  useEffect(() => {
    const {
      notesDocument: existingNotesDocument,
      attachments: existingAttachments,
    } = mergePatientTextDocuments(documentsData?.encounterDocuments || []);
    setNotesDocument(existingNotesDocument);
    setAttachments(
      existingAttachments.map((attachment) => ({
        document: attachment,
      }))
    );
  }, [!!documentsData?.encounterDocuments]);

  useEffect(() => {
    onOpenChange?.(!!isOpen);
  }, [!!isOpen]);

  useEffect(() => {
    onChange?.({
      documents: attachments.map(
        ({ document }) => document
      ) as PatientDocument[],
      notes: notesDocument,
    });
  }, [attachments, notesDocument]);

  const renderAttachment = (
    attachment: (typeof attachments)[number],
    index: number
  ) => {
    const { document, isLoading, isError } = attachment;
    return (
      <PatientFileDocument
        isLoading={isLoading}
        hasError={isError}
        source={source}
        document={document as PatientDocument}
        onUpdate={(updatedAttachment) => {
          setAttachments((prev) =>
            prev.map((attachment, i) =>
              i === index ? { ...attachment, ...updatedAttachment } : attachment
            )
          );
          onDocumentsChange?.();
        }}
        onDelete={deleteDocument}
        sendEvents={true}
      />
    );
  };

  const renderTrigger = () => {
    if (getTrigger) {
      return getTrigger({
        attachments,
        notesDocument,
        isLoading: isLoadingDocuments,
        isOpen,
        setIsOpen,
      });
    }

    if (attachments.length > 0 || notesDocument.content) {
      return (
        <Badge
          className="z-10"
          count={attachments.length + (notesDocument.content ? 1 : 0)}
          offset={[-4, 10]}
        >
          <Button
            variant="outlined"
            icon={
              isLoadingDocuments ? <LoadingOutlined /> : <FileAttachment02 />
            }
            onClick={() => setIsOpen(true)}
          >
            Documents attached
          </Button>
        </Badge>
      );
    }

    return (
      <Button
        variant="outlined"
        icon={isLoadingDocuments ? <LoadingOutlined /> : <FileAttachment02 />}
        onClick={() => setIsOpen(true)}
      >
        Add documents
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}
      <StyledModal
        title="Patient notes & documents"
        open={isOpen}
        size="2xl"
        onClose={() => {
          setIsOpen(false);
        }}
        footer={({ secondaryAction }) => {
          return (
            <div className="flex justify-end">
              <Button {...secondaryAction} />
            </div>
          );
        }}
        secondaryAction={{
          label: 'Save History',
        }}
        extras={
          attachments.length > 0 && (
            <Button
              appearance="danger"
              icon={<Trash01 />}
              onClick={deleteAllDocuments}
            >
              Delete all documents
            </Button>
          )
        }
      >
        <div className="flex flex-col gap-4 h-full">
          <div className="flex-1 h-full">
            <Input.TextArea
              placeholder="Add notes"
              autoFocus
              style={{ resize: 'none', height: '100%' }}
              defaultValue={notesDocument.content}
              onChange={(e) => {
                setNotesDocument((prev) => ({
                  ...prev,
                  content: e.target.value,
                }));
              }}
              onBlur={async (e) => {
                await updateNotesDocument(e.target.value);
              }}
            />
            <div
              className={`text-sm mt-1 text-gray-500 flex flex-wrap gap-2 ${
                notesDocument.content?.length > maxAllowedNotesLength
                  ? 'text-red-500 justify-between'
                  : 'justify-end'
              }`}
            >
              {notesDocument.content?.length > maxAllowedNotesLength && (
                <span>
                  You have exceeded the maximum allowed character limit. Changes
                  will not be saved.
                </span>
              )}
              <span className="text-right">
                {notesDocument.content?.length || 0} / {maxAllowedNotesLength}{' '}
                {' characters'}
              </span>
            </div>
          </div>
          <Divider style={{ marginBottom: -10 }}>
            <p className="text-sm text-gray-500">
              {attachments.length || 'Add'} attachment(s)
            </p>
          </Divider>
          {attachments?.length > 0 && (
            <div className="flex gap-4 overflow-y-auto shrink-0">
              {attachments.map(renderAttachment)}
            </div>
          )}
          <Upload.Dragger
            fileList={[]}
            name="files"
            accept=".doc,.docx,.txt,.pdf"
            beforeUpload={() => {
              return false;
            }}
            onChange={async ({ file }) => {
              if (await validateFile(file as unknown as File)) {
                await createFileDocument(file as unknown as File);
              }
            }}
            // @ts-expect-error
            onClick={() => {
              analytics.track(
                `Clicked Draggable Area In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History Modal`,
                {
                  email: user?.email,
                }
              );
            }}
            onDrop={() => {
              analytics.track(
                `Dropped File In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History Modal`,
                {
                  email: user?.email,
                }
              );
            }}
          >
            <p className="ant-upload-drag-icon">
              {isLoadingDocuments ? <LoadingOutlined /> : <InboxOutlined />}
            </p>
            <p className="ant-upload-text text-[#00d090]">
              Click or drag file to this area to upload
              <p className="text-xs text-gray-500">txt, doc, docx, pdf</p>
            </p>
          </Upload.Dragger>
        </div>
      </StyledModal>
    </>
  );
};
